import {
    Box,
    Button,
    Chip,
    Container,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { SettingsOutlined } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { ROOM_TYPES, roomPath, roomTypeLabel, type RoomType } from "@/deal-room/room-path";
import { ds } from "@/deal-room/theme/ds";

import { BisonLogo } from "@/deal-room/layout/BisonLogo";

import { AccessPanel } from "./AccessPanel";
import { formatDay } from "./format";
import { bcRow, bcTableFrame, bcTd, bcTh, siteInput } from "./table-styles";
import { SignIn } from "./SignIn";

interface AuthMeResponse {
    user: {
        email: string;
        name?: string;
        slug?: string;
        staff: boolean;
    } | null;
}

interface DirectoryRow {
    slug: string;
    roomType: RoomType;
    supplierName?: string;
    owner?: { name?: string; email?: string };
    recipients: number;
    lastOpened?: string;
    pendingRequests: number;
}

interface DirectoryResponse {
    slugs: string[];
    rooms?: DirectoryRow[];
}

// Root landing.
//
// Three-way branch on the session:
//   - No session              → render SignIn form inline (same content as
//                               /sign-in, so an anon visitor can't enumerate
//                               other paths from here).
//   - Magic-link recipient    → redirect to their room. They have no business
//                               browsing a directory; their session is bound
//                               to one room.
//   - Bison staff (AAD)       → the staff directory: every room, and where a
//                               room's recipients and invites are managed
//                               (2026-09-24, Bill).
export function Home() {
    const me = useQuery({
        queryKey: ["auth-me"],
        queryFn: async (): Promise<AuthMeResponse> => {
            const res = await fetch("/api/auth/me", { credentials: "include" });
            if (res.status === 401) return { user: null };
            if (!res.ok) throw new Error(`me failed: ${res.status}`);
            return res.json();
        },
        staleTime: 30_000,
    });

    if (me.isLoading) {
        return (
            <Container maxWidth="md" sx={{ py: 6 }}>
                <Typography color="text.secondary">Loading…</Typography>
            </Container>
        );
    }

    if (me.isError || !me.data?.user) {
        // Render the SignIn page inline so visitors hitting the bare root
        // get the same form as /sign-in.
        return <SignIn />;
    }

    const user = me.data.user;

    if (!user.staff && user.slug) {
        return <Navigate to={roomPath(user.slug)} replace />;
    }

    if (!user.staff) {
        return <NoDirectoryNotice email={user.email} />;
    }

    return <StaffRoomsList />;
}

function NoDirectoryNotice({ email }: { email: string }) {
    return (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Stack spacing={2} alignItems="flex-start">
                <Typography variant="overline" color="text.secondary">
                    Bison Commerce rooms
                </Typography>
                <Typography variant="h1" sx={{ fontSize: "2rem" }}>
                    You're signed in, but there's nothing here.
                </Typography>
                <Typography color="text.secondary">
                    Open the link your Bison contact sent to {email}, or sign
                    out and use a different email.
                </Typography>
                <Box
                    component="a"
                    href="/.auth/logout?post_logout_redirect_uri=/sign-in"
                    sx={{
                        mt: 1,
                        fontSize: 14,
                        color: "primary.main",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                    }}
                >
                    Sign out
                </Box>
            </Stack>
        </Container>
    );
}

const th = bcTh;
const td = bcTd;

// The staff directory: one grid, every room, a side panel per row for access.
function StaffRoomsList() {
    const navigate = useNavigate();
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ["rooms-list"],
        queryFn: async (): Promise<DirectoryResponse> => {
            const res = await fetch("/api/rooms", { credentials: "include" });
            if (res.status === 401) {
                navigate("/sign-in", { replace: true });
                return { slugs: [] };
            }
            if (!res.ok) throw new Error(`list failed: ${res.status}`);
            return res.json();
        },
    });

    // Belt-and-braces: if the staff role check ever drifts, redirect on 401.
    useEffect(() => {
        if (error && /401/.test(String(error))) navigate("/sign-in", { replace: true });
    }, [error, navigate]);

    const [query, setQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState<RoomType | "all">("all");
    const [open, setOpen] = useState<string | null>(null);

    const q = query.trim().toLowerCase();
    const all = data?.rooms ?? data?.slugs.map((slug): DirectoryRow => ({ slug, roomType: "deal_room", recipients: 0, pendingRequests: 0 })) ?? [];
    const countOf = (t: RoomType) => all.filter((r) => r.roomType === t).length;
    const rows = all
        .filter((r) => typeFilter === "all" || r.roomType === typeFilter)
        .filter((r) => !q || r.slug.includes(q) || (r.supplierName ?? "").toLowerCase().includes(q) || roomTypeLabel(r.roomType).includes(q))
        .sort((a, b) => (a.supplierName ?? a.slug).localeCompare(b.supplierName ?? b.slug));

    // Filter pills: the room types, each with its count; one selected at a time.
    const pill = (label: string, value: RoomType | "all", count: number) => {
        const selected = typeFilter === value;
        return (
            <Chip
                key={value}
                label={`${label} · ${count}`}
                onClick={() => setTypeFilter(value)}
                variant="outlined"
                sx={{
                    height: 28,
                    fontSize: ds.text.xs,
                    fontWeight: 600,
                    borderRadius: "100px",
                    bgcolor: selected ? ds.info.bg : ds.white,
                    color: selected ? ds.info.text : ds.gray600,
                    border: "1px solid",
                    borderColor: selected ? ds.info.text : ds.gray200,
                    "&:hover": { bgcolor: selected ? ds.info.bg : ds.gray50 },
                }}
            />
        );
    };

    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Box sx={{ mb: 3 }}>
                <BisonLogo width={150} />
            </Box>
            <Typography variant="h1" sx={{ fontSize: "2.5rem", mb: 1 }}>
                Rooms
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
                Every live room. Open one to see it the way the supplier does, or manage who can sign in.
            </Typography>

            <TextField
                type="search"
                placeholder="Search rooms"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                fullWidth
                size="small"
                autoFocus
                slotProps={{
                    htmlInput: { "aria-label": "Search rooms", "data-1p-ignore": true, "data-lpignore": "true" },
                }}
                sx={{ ...siteInput, mb: 2 }}
            />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
                {pill("All rooms", "all", all.length)}
                {ROOM_TYPES.map((t) => pill(roomTypeLabel(t).replace(/^./, (c) => c.toUpperCase()), t, countOf(t)))}
            </Stack>

            {isLoading ? (
                <Typography color="text.secondary">Loading…</Typography>
            ) : error ? (
                <Typography color="error">Could not load rooms.</Typography>
            ) : !rows.length ? (
                <Box
                    sx={{
                        p: 4,
                        textAlign: "center",
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: "10px",
                    }}
                >
                    <Typography color="text.secondary">
                        {q || typeFilter !== "all" ? "No rooms match." : "No rooms yet."}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ ...bcTableFrame, overflowX: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={th}>Supplier</TableCell>
                                <TableCell sx={th}>Slug</TableCell>
                                <TableCell sx={th}>Type</TableCell>
                                <TableCell sx={th}>Recipients</TableCell>
                                <TableCell sx={th}>Last opened</TableCell>
                                <TableCell sx={th}>Room owner</TableCell>
                                <TableCell sx={th} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.slug} sx={bcRow}>
                                    <TableCell sx={{ ...td, fontWeight: 600 }}>
                                        <Box
                                            component="a"
                                            href={roomPath(r.slug, true, r.roomType)}
                                            sx={{ color: ds.navy, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                                        >
                                            {r.supplierName ?? r.slug}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ ...td, color: ds.gray500 }}>{r.slug}</TableCell>
                                    <TableCell sx={td}>{roomTypeLabel(r.roomType)}</TableCell>
                                    <TableCell sx={{ ...td, fontVariantNumeric: "tabular-nums" }}>
                                        {r.recipients}
                                        {r.pendingRequests > 0 && (
                                            <Box component="span" sx={{ ml: 1, color: ds.warning.text, fontSize: ds.text.xs }}>
                                                {r.pendingRequests} asked
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell sx={td}>{r.lastOpened ? formatDay(r.lastOpened) : "Not yet"}</TableCell>
                                    <TableCell sx={td}>{r.owner?.name ?? ""}</TableCell>
                                    <TableCell sx={{ ...td, whiteSpace: "nowrap" }}>
                                        <Button size="small" variant="outlined" href={roomPath(r.slug, true, r.roomType)}>
                                            Open
                                        </Button>
                                        <Tooltip title="Access">
                                            <IconButton
                                                size="small"
                                                aria-label="Access"
                                                onClick={() => setOpen(r.slug)}
                                                sx={{ ml: 1, color: ds.gray600 }}
                                            >
                                                <SettingsOutlined fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}

            <AccessPanel slug={open} onClose={() => setOpen(null)} onChanged={() => void refetch()} />
        </Container>
    );
}
